/* eslint-disable camelcase */
import { CompositeScreenProps } from "@react-navigation/native"
import { FormikProps, FormikValues } from "formik"
import { observer } from "mobx-react-lite"
import { Modal } from "native-base"
import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { Alert, Appearance, Platform, View, ViewStyle } from "react-native"
import { Button, ChangePasswordForm, JalaIcon, Screen, Text } from "../../../../components"
import { translate, TxKeyPath } from "../../../../i18n"
import { useStores } from "../../../../models"
import { goBack, navigate } from "../../../../navigators"
import { color } from "../../../../theme"

const ROOT: ViewStyle = {
  flexGrow: 1,
  paddingVertical: 16,
  paddingHorizontal: 16
}

const REGISTER_BUTTON: ViewStyle = {
  marginVertical: 14,
}

const BTN_WRAPPER: ViewStyle = {
  justifyContent: 'flex-end',
  flex: 1
}

const MODAL: ViewStyle = {
  width: 260,
  borderRadius: 14,
  padding: 8,
  paddingBottom: 14,
  paddingHorizontal: 21,
  backgroundColor: color.palette.white,
  alignItems: 'center'
}

const ICON_WRAPPER = (isError: boolean): ViewStyle => ({
  borderRadius: 50,
  borderWidth: 8,
  borderColor: isError ? color.palette.red500 : color.palette.green400,
  marginTop: 16
})

const FORGOT: ViewStyle = {
  alignSelf: 'flex-end',
}

const SPACE: ViewStyle = {
  flex: 1,
}

export interface PopupProps {
  colorScheme?: "light" | "dark",
  isVisible?: boolean,
  error?: boolean,
  title?: TxKeyPath,
  message?: any,
  onPress?: () => void,
  textComponent?: any,
  btnTx?: TxKeyPath
}

export const PopUp = (props: PopupProps ) => {
  const {
    isVisible, message, error = null, btnTx, onPress, colorScheme,
    title, textComponent
  } = props;
  // const isError = error !== null;
  const iconColor = error ? color.palette.red500 : color.palette.green400;
  const icon = error ? "delete": "check";

  return (
    <Modal isOpen={isVisible}>
      <View style={MODAL}>
        <View style={ICON_WRAPPER(error)}>
          <JalaIcon size={60} name={icon} color={iconColor} />
        </View>
        {(textComponent || message) && (
          <>
            {textComponent || (
              <View>
                {title && <Text tx={title} preset="display2" textAlign="center" mt="14px" colorScheme={colorScheme} />}
                <Text text={message} preset="body2" textAlign="center" mb="16px" mt="18px" colorScheme={colorScheme} />
              </View>
            )}
          </>
        )}
        <Button testID="jala:id/action-button" preset="link" tx={btnTx} textPreset="title4" onPress={onPress} colorScheme={colorScheme} />
      </View>
    </Modal>
  )
}

export const ChangePasswordScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function ChangePasswordScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const [ isError, setIsError ] = useState<boolean>(true)
  const [ message, setMessage ] = useState<any>(null)
  const [ isVisible, setVisible ] = useState<boolean>(false);

  // Pull in one of our MST stores
  const { userStore, userStore: { loading: userLoading, error: userError } } = useStores()

  const changePasswordForm = useRef<FormikProps<FormikValues>>()
  const changePasswordFormRef = useCallback((node) => {
    changePasswordForm.current = node;
    if (changePasswordForm.current) setIsError(!(changePasswordForm.current.dirty && changePasswordForm.current.isValid))
  }, [])

  useEffect(() => {
    if (!userStore) return;
    if (userError) {
      if (userError.kind === 'rejected' && userError.response.status === 422) {
        const errors = {}
        Object.keys(userError.response.data.errors).forEach((key) => {
          errors[key] = (userError.response.data.errors[key] || [])[0]
        })
        if ('password_old' in errors)
          setMessage(translate("changePasswordForm.oldPasswordError"))
        else setMessage(translate("changePasswordForm.failedInput"))
        if (changePasswordForm.current) changePasswordForm.current.setErrors(errors)
        userStore.set('error', null)
      }
      else {
        if (Platform.OS === 'web') {
          if (userError.response)
            confirm(userError.response.originalError.message)
          userStore.set('error', null)
        }
        else
          Alert.alert(
            userError.response.originalError.message,
            undefined,
            [ { onPress: () => userStore.set('error', null) } ]
          );
      }
    } else setMessage(translate("changePasswordForm.success"));
  }, [userError, userStore])

  const onSubmit = ({ current_password, password, password_confirmation }) => {
    const params = {
      password_old: current_password,
      password,
      password_confirmation,
      _method: 'PUT'
    }
    if (userLoading) return;
    // userStore.changePassword(params).then(() => goBack('Profile.Stack'));
    userStore.changePassword(params).then(() => setVisible(true))
  }

  const handleSubmit = (event) => {
    if (changePasswordForm.current) changePasswordForm.current.handleSubmit(event)
  }

  const handleButton = () => {
    if (userError) setVisible(false);
    else {
      setVisible(false);
      goBack('Profile.Stack')
    }
  }

  // const PopUp = ({ isVisible, onPress } : { isVisible?: boolean, onPress?: () => void }) => {
  //   const isError = userError !== null;
  //   const iconColor = isError ? color.palette.red500 : color.palette.green400;
  //   const icon = isError ? "delete": "check";

  //   return (
  //     <Modal isOpen={isVisible}>
  //       <View style={MODAL}>
  //         <View style={ICON_WRAPPER(isError)}>
  //           <JalaIcon size={60} name={icon} color={iconColor} />
  //         </View>
  //         <Text text={message} preset="display2" textAlign="center" mb="16px" mt="18px" colorScheme={colorScheme} />
  //         <Button testID="jala:id/ok-button" preset="link" tx="common.ok" textPreset="title4" onPress={onPress} colorScheme={colorScheme} />
  //       </View>
  //     </Modal>
  //   )
  // }

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={color.primary}
      statusBar="light-content"
    >
      <ChangePasswordForm colorScheme={colorScheme} onSubmit={onSubmit} innerRef={changePasswordFormRef} />
      <Button
        testID="jala:id/forgot-password-button"
        tx="loginScreen.forgotPassword"
        preset="link"
        colorScheme={colorScheme}
        textPreset="title4"
        style={FORGOT}
        onPress={() => navigate('Profile.Stack', 'Profile.Password.Reset')}
      />
      <View style={SPACE} />
      <View style={BTN_WRAPPER}>
        <Button
          testID="jala:id/change-password-button"
          tx="save"
          colorScheme={colorScheme}
          style={REGISTER_BUTTON}
          disabled={isError}
          loading={userLoading}
          onPress={handleSubmit}
        />
      </View>
      <PopUp
        isVisible={isVisible}
        onPress={handleButton}
        error={(userError !== null)}
        message={message}
        btnTx="common.ok"
        colorScheme={colorScheme}
      />
    </Screen>
  )
})

